import { z } from 'zod';
import {
  emailValidator,
  firstNameValidator,
  fullNameValidator,
  honeypotAddressFieldValidator,
  lastNameValidator,
  ownHomeValidator,
  phoneValidator,
  zipCodeValidator,
} from '@/utils/validation/inputs';

export enum LeadFormSchemaMode {
  SEPARATE_NAMES_WITH_EMAIL = 'separateNamesWithEmail',
  SEPARATE_NAMES_WITHOUT_EMAIL = 'separateNamesWithoutEmail',
  FULL_NAME_WITH_EMAIL = 'fullNameWithEmail',
  FULL_NAME_WITHOUT_EMAIL = 'fullNameWithoutEmail',
}

const commonFieldValidators = {
  ...zipCodeValidator,
  ...phoneValidator,
  ...ownHomeValidator,
  ...honeypotAddressFieldValidator,
};

// Base schemas without email
export const separateNamesWithoutEmailSchema = z.object({
  mode: z.literal(LeadFormSchemaMode.SEPARATE_NAMES_WITHOUT_EMAIL),
  ...firstNameValidator,
  ...lastNameValidator,
  ...commonFieldValidators,
});

export const fullNameWithoutEmailSchema = z.object({
  mode: z.literal(LeadFormSchemaMode.FULL_NAME_WITHOUT_EMAIL),
  ...fullNameValidator,
  ...commonFieldValidators,
});

// Derived schemas with email added
export const separateNamesWithEmailSchema = separateNamesWithoutEmailSchema
  .omit({ mode: true })
  .extend({
    mode: z.literal(LeadFormSchemaMode.SEPARATE_NAMES_WITH_EMAIL),
    ...emailValidator,
  });

export const fullNameWithEmailSchema = fullNameWithoutEmailSchema
  .omit({ mode: true })
  .extend({
    mode: z.literal(LeadFormSchemaMode.FULL_NAME_WITH_EMAIL),
    ...emailValidator,
  });

export const leadFormSchema = z.discriminatedUnion('mode', [
  separateNamesWithEmailSchema,
  separateNamesWithoutEmailSchema,
  fullNameWithEmailSchema,
  fullNameWithoutEmailSchema,
]);

type BaseLeadFormSchemaType = z.infer<typeof leadFormSchema>;
export type LeadFormSchemaType = BaseLeadFormSchemaType & {
  tcpaAcknowledgment?: boolean;
};

export const shouldShowFullName = (mode: LeadFormSchemaMode): boolean =>
  mode === LeadFormSchemaMode.FULL_NAME_WITH_EMAIL ||
  mode === LeadFormSchemaMode.FULL_NAME_WITHOUT_EMAIL;

export const shouldShowEmail = (mode: LeadFormSchemaMode): boolean =>
  mode === LeadFormSchemaMode.SEPARATE_NAMES_WITH_EMAIL ||
  mode === LeadFormSchemaMode.FULL_NAME_WITH_EMAIL;
